import { useContext, useEffect, useState } from "react";
import {
    Box,
    Button,
    Input,
    VStack,
    Text,
    useToast,
    Textarea,
    Heading,
    HStack,
    Card,
    CardHeader,
    CardBody,
    Divider,
    InputGroup,
    InputLeftElement,
    Badge,
    Flex,
    Tooltip,
    IconButton,
    Switch,
    FormControl,
    FormLabel,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from "@chakra-ui/react";
import { SearchIcon, DownloadIcon, CopyIcon, ViewIcon, SettingsIcon } from "@chakra-ui/icons";
import { appRequest } from "routes";
import axios from "axios";
import { AppContext } from "AppProvider/AppContext";

const SapXmlPayload = () => {
    const { accountDetails, getAccountDetails, workflowProcess } = useContext(AppContext)
    const [documentId, setDocumentId] = useState("");
    const [xmlData, setXmlData] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSapEnabled, setIsSapEnabled] = useState(accountDetails?.sap ?? false);
    const [tempSapStatus, setTempSapStatus] = useState(accountDetails?.sap ?? false);
    const [sapMessage, setSapMessage] = useState(accountDetails?.sapUpdateMessage ?? "");
    const [isUpdatingSap, setIsUpdatingSap] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (accountDetails) {
            setIsSapEnabled(accountDetails?.sap ?? false)
            setTempSapStatus(accountDetails?.sap ?? false)
            setSapMessage(accountDetails?.sapUpdateMessage ?? "")
        }
    }, [accountDetails])

    const handleSapStatusToggle = () => {
        setTempSapStatus(!tempSapStatus);
    };

    const handleUpdateStatus = async () => {
        if (!sapMessage.trim()) {
            toast({
                title: "Message required",
                description: "Please provide a reason for updating the SAP status.",
                status: "warning",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
            return;
        }

        setIsUpdatingSap(true);

        try {
            const response: any = await appRequest("user", "updateSapStatus", {
                sap: tempSapStatus,
                message: sapMessage
            });

            if (response && response.status === "success") {
                setIsSapEnabled(tempSapStatus);
                await getAccountDetails();
                toast({
                    title: "SAP Status Updated",
                    description: `SAP integration has been ${tempSapStatus ? "enabled" : "disabled"}.`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
            } else {
                throw new Error(response?.message || "Failed to update SAP status");
            }
        } catch (error: any) {
            toast({
                title: "Update Failed",
                description: error.message || "Unable to update SAP status.",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsUpdatingSap(false);
        }
    };

    const formatXml = (xml: string) => {
        let formatted = '';
        let reg = /(>)(<)(\/*)/g;
        xml = xml.replace(reg, '$1\r\n$2$3');
        let pad = 0;
        xml.split('\r\n').forEach((node) => {
            let indent = 0;
            if (node.match(/.+<\/\w[^>]*>$/)) {
                indent = 0;
            } else if (node.match(/^<\/\w/)) {
                if (pad !== 0) {
                    pad -= 1;
                }
            } else if (node.match(/^<\w[^>]*[^\/]>.*$/)) {
                indent = 1;
            } else {
                indent = 0;
            }

            let padding = '';
            for (let i = 0; i < pad; i++) {
                padding += '  ';
            }

            formatted += padding + node + '\r\n';
            pad += indent;
        });
        return formatted;
    };

    const handleFetch = async () => {
        if (!documentId) {
            toast({
                title: "Input missing",
                description: "Please enter a valid Document ID to proceed.",
                status: "warning",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
            return;
        }

        setIsLoading(true);
        setXmlData("");

        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/document/getSapXmlPayload`;

            const response = await axios.post(url, { documentId });

            let rawData = response.data;
            let xmlString = "";

            // Handle different response structures
            if (typeof rawData === 'object' && rawData !== null) {
                if (rawData.payload && typeof rawData.payload === 'object' && rawData.payload.payload) {
                    // Structure: { payload: { payload: "<xml>..." } }
                    xmlString = rawData.payload.payload;
                } else if (rawData.payload && typeof rawData.payload === 'string') {
                    // Structure: { payload: "<xml>..." }
                    xmlString = rawData.payload;
                } else {
                    // Fallback: stringify the whole object if standard structure not found
                    xmlString = JSON.stringify(rawData, null, 2);
                }
            } else {
                xmlString = String(rawData);
            }

            try {
                // Attempt to format if it looks like XML
                if (xmlString.trim().startsWith('<')) {
                    setXmlData(formatXml(xmlString));
                } else {
                    setXmlData(xmlString);
                }
            } catch (e) {
                setXmlData(xmlString);
            }

            toast({
                title: "Payload Fetched",
                description: "The XML payload has been successfully retrieved.",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        } catch (error: any) {
            console.error("Fetch error:", error);
            toast({
                title: "Fetch Failed",
                description: error.message || "Unable to retrieve XML payload.",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!xmlData) return;

        const element = document.createElement("a");
        const file = new Blob([xmlData], { type: "text/plain" });
        element.href = URL.createObjectURL(file);
        element.download = `${documentId || "payload"}.txt`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        toast({
            title: "Download Started",
            status: "info",
            duration: 2000,
            isClosable: true,
            position: "top-right",
        });
    };

    const handleCopy = () => {
        if (!xmlData) return;
        navigator.clipboard.writeText(xmlData);
        toast({
            title: "Copied to Clipboard",
            status: "success",
            duration: 2000,
            isClosable: true,
            position: "top-right",
        });
    };

    return (
        <Box p={4} bg="gray.50" minH="100vh">
            <Card mx="auto" shadow="lg" borderRadius="lg" bg="white">
                <CardHeader pb={0}>
                    <Flex align="center" justify="space-between">
                        <HStack spacing={3}>
                            <ViewIcon color="teal.500" boxSize={6} />
                            <Heading size="md" color="gray.700">SAP XML Payload Viewer</Heading>
                        </HStack>
                        <HStack display={workflowProcess?.processName === "foreign" ? 'none' : undefined} spacing={4}>
                            <Badge colorScheme={isSapEnabled ? "green" : "red"} variant="subtle" px={2} borderRadius="full">
                                SAP: {isSapEnabled ? "Enabled" : "Disabled"}
                            </Badge>
                            <Badge colorScheme="teal" variant="subtle" px={2} borderRadius="full">
                                Live Fetch
                            </Badge>
                        </HStack>
                    </Flex>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                        Retrieve and inspect XML payloads by Document ID.
                    </Text>
                </CardHeader>

                <Divider color="gray.200" my={4} />

                <CardBody>
                    <Tabs variant="enclosed" colorScheme="teal">
                        <TabList mb={4}>
                            <Tab fontWeight="bold">
                                <HStack spacing={2}>
                                    <ViewIcon />
                                    <Text>SAP API</Text>
                                </HStack>
                            </Tab>
                            <Tab display={workflowProcess?.processName === "foreign" ? 'none' : undefined} fontWeight="bold">
                                <HStack spacing={2}>
                                    <SettingsIcon />
                                    <Text>SAP Update</Text>
                                </HStack>
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* Tab 1: SAP API Viewer */}
                            <TabPanel p={0} pt={4}>
                                <VStack spacing={6} align="stretch">
                                    <Box>
                                        <Text mb={2} fontWeight="medium" color="gray.600">Document ID</Text>
                                        <Flex gap={2}>
                                            <InputGroup size="md">
                                                <InputLeftElement pointerEvents="none">
                                                    <SearchIcon color="gray.400" />
                                                </InputLeftElement>
                                                <Input
                                                    placeholder="Enter Document ID (e.g., DI-6142011229)"
                                                    value={documentId}
                                                    onChange={(e) => setDocumentId(e.target.value)}
                                                    bg="gray.50"
                                                    _focus={{ bg: "white", borderColor: "teal.400", boxShadow: "0 0 0 1px teal.400" }}
                                                />
                                            </InputGroup>
                                            <Button
                                                colorScheme="teal"
                                                onClick={handleFetch}
                                                isLoading={isLoading}
                                                loadingText="Fetching"
                                                px={6}
                                                leftIcon={<SearchIcon />}
                                            >
                                                Fetch
                                            </Button>
                                        </Flex>
                                    </Box>

                                    {xmlData && (
                                        <Box>
                                            <Flex justify="space-between" align="center" mb={2}>
                                                <Text fontWeight="medium" color="gray.600">Payload Content</Text>
                                                <HStack spacing={2}>
                                                    <Tooltip label="Copy" hasArrow>
                                                        <IconButton
                                                            aria-label="Copy to clipboard"
                                                            icon={<CopyIcon />}
                                                            size="sm"
                                                            variant="ghost"
                                                            colorScheme="gray"
                                                            onClick={handleCopy}
                                                        />
                                                    </Tooltip>
                                                    <Button
                                                        size="sm"
                                                        colorScheme="blue"
                                                        variant="outline"
                                                        leftIcon={<DownloadIcon />}
                                                        onClick={handleDownload}
                                                    >
                                                        Download .txt
                                                    </Button>
                                                </HStack>
                                            </Flex>
                                            <Textarea
                                                value={xmlData}
                                                readOnly
                                                height="450px"
                                                fontFamily="'Fira Code', 'Courier New', monospace"
                                                fontSize="sm"
                                                bg="gray.900"
                                                color="green.300"
                                                p={4}
                                                borderRadius="md"
                                                border="none"
                                                _focus={{ boxShadow: "none" }}
                                                resize="vertical"
                                                sx={{
                                                    "::-webkit-scrollbar": {
                                                        width: "8px",
                                                        height: "8px",
                                                    },
                                                    "::-webkit-scrollbar-thumb": {
                                                        background: "gray.600",
                                                        borderRadius: "4px",
                                                    },
                                                    "::-webkit-scrollbar-track": {
                                                        background: "gray.800",
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {!xmlData && !isLoading && (
                                        <Flex
                                            direction="column"
                                            align="center"
                                            justify="center"
                                            h="300px"
                                            bg="gray.50"
                                            borderRadius="md"
                                            border="1px dashed"
                                            borderColor="gray.300"
                                            color="gray.400"
                                        >
                                            <SearchIcon boxSize={8} mb={2} />
                                            <Text>Enter a Document ID to view the payload.</Text>
                                        </Flex>
                                    )}
                                </VStack>
                            </TabPanel>

                            {/* Tab 2: SAP Update Settings */}
                            <TabPanel p={0} pt={4}>
                                <VStack spacing={6} align="stretch">
                                    <Box p={5} bg="teal.50" borderRadius="md" border="1px" borderColor="teal.100" shadow="sm">
                                        <Flex align="center" justify="space-between" mb={6}>
                                            <HStack spacing={3}>
                                                <SettingsIcon color="teal.600" boxSize={5} />
                                                <Box>
                                                    <Text fontWeight="bold" color="teal.800">SAP Integration Status</Text>
                                                    <Text fontSize="sm" color="teal.600">Enable or disable SAP functionality globally</Text>
                                                </Box>
                                            </HStack>
                                            <FormControl display="flex" alignItems="center" width="auto">
                                                <FormLabel htmlFor="sap-status" mb="0" mr={3} fontWeight="bold">
                                                    {tempSapStatus ? "ENABLED" : "DISABLED"}
                                                </FormLabel>
                                                <Switch
                                                    id="sap-status"
                                                    colorScheme="teal"
                                                    size="lg"
                                                    isChecked={tempSapStatus}
                                                    onChange={handleSapStatusToggle}
                                                    isDisabled={isUpdatingSap}
                                                />
                                            </FormControl>
                                        </Flex>
                                        <Divider borderColor="teal.200" mb={6} />
                                        <Box>
                                            <Text fontSize="sm" fontWeight="bold" color="teal.700" mb={2}>UPDATE MESSAGE / REASON</Text>
                                            <Textarea
                                                placeholder="Explain why the SAP status is being changed..."
                                                value={sapMessage}
                                                onChange={(e) => setSapMessage(e.target.value)}
                                                bg="white"
                                                borderRadius="md"
                                                minH="120px"
                                                _focus={{ borderColor: "teal.400", boxShadow: "0 0 0 1px teal.400" }}
                                            />
                                            <Button
                                                mt={6}
                                                colorScheme="teal"
                                                size="lg"
                                                onClick={handleUpdateStatus}
                                                isLoading={isUpdatingSap}
                                                loadingText="Updating"
                                                leftIcon={<SettingsIcon />}
                                                width="full"
                                                shadow="md"
                                            >
                                                Save SAP Status Change
                                            </Button>
                                        </Box>
                                    </Box>
                                </VStack>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </CardBody>
            </Card>
        </Box>
    );
};

export default SapXmlPayload;